import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BillingInfoService } from '@shared/services/billing-info.service';
import { BillingStore } from '@app/interfaces/billing-store.interface';
import { BillingStoreService } from '@app/home/store/billing-store.service';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { AuthenticationService, UserProfile } from '@app/authentication/authentication.service';
import { ThemingService } from '@shared/services/theming.service';
import { Theme } from '@shared/themes/default.theme';
import { constants } from '@app/@shared/constants';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { QuotePreparationService } from '@shared/components/quote-preparation/quote-preparation.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-policy',
  templateUrl: './confirm.component.html',
  styleUrls: ['./confirm.component.scss'],
  standalone: false,
})
export class ConfirmComponent implements OnInit, OnDestroy {
  policyStore: BillingStore = {};

  theme: Theme;
  isSmallScreen = false;
  waitingForQtd: boolean = false;
  showEmailConfirmDialog: boolean = false;
  private destroy$: Subject<void> = new Subject<void>();
  //quote status, if bound send to payment, if issued show info screen

  constructor(
    private fb: UntypedFormBuilder,
    private billingInfoService: BillingInfoService,
    private billingStoreService: BillingStoreService,
    private route: ActivatedRoute,
    private router: Router,
    private authenticationService: AuthenticationService,
    private themeService: ThemingService,
    private breakpointObserver: BreakpointObserver,
    private quotePreparationService: QuotePreparationService
  ) {
    this.breakpointObserver.observe([Breakpoints.Handset]).subscribe((result) => {
      this.isSmallScreen = result.matches;
    });
  }

  get userProfile(): UserProfile {
    return this.authenticationService.userProfile;
  }

  d2cTheme: Theme;

  ngOnInit() {
    this.policyStore = this.billingStoreService.getBillingStore();

    if (!this.policyStore) {
      this.router.navigate(['direct', 'policy']); //go back to beginning if there is no policy in the billing store
    }

    this.themeService.themeChange.subscribe((theme: Theme) => {
      console.log('theme', theme);
      this.theme = theme;
    });

    this.quotePreparationService.currentQuoteStatus$.pipe(takeUntil(this.destroy$)).subscribe((status) => {
      if (!this.waitingForQtd) {
        return;
      }

      if (status === 'QTD') {
        this.waitingForQtd = false;
        this.quotePreparationService.stopIPGPreparing();
        this.router.navigate(['direct', 'payment']);
      }
    });

    this.d2cTheme = JSON.parse(JSON.stringify(this.theme));
    if (this.d2cTheme.name != 'appalachian') {
      this.d2cTheme.agent.phone =
        this.policyStore.product == 'ea' ? constants.contactForCanopy : constants.contactForNationwideD2C;
    }
    // console.log('this.theme', this.theme);
  }

  bindForm = this.fb.group({
    affirm: ['', Validators.required],
    autopay: ['', Validators.required],
    legal: ['', Validators.required],
    legal2: ['', Validators.required],
    confirm: ['', Validators.required],
    legal1: ['', Validators.required],
  });

  emailConfirmForm = this.fb.group({
    emailAddress: ['', [Validators.required, Validators.email]],
    confirmEmailAddress: ['', [Validators.required, Validators.email]],
  });

  testManualQuoteStatus: string = '';

  get emailsMatch(): boolean {
    const emailAddress = (this.emailConfirmForm.get('emailAddress')?.value || '').trim().toLowerCase();
    const confirmEmailAddress = (this.emailConfirmForm.get('confirmEmailAddress')?.value || '').trim().toLowerCase();
    return !!emailAddress && emailAddress === confirmEmailAddress;
  }

  get showEmailMismatchError(): boolean {
    const emailAddressControl = this.emailConfirmForm.get('emailAddress');
    const confirmEmailAddressControl = this.emailConfirmForm.get('confirmEmailAddress');
    const shouldShowError =
      (emailAddressControl?.dirty || emailAddressControl?.touched || false) &&
      (confirmEmailAddressControl?.dirty || confirmEmailAddressControl?.touched || false);
    return shouldShowError && !this.emailsMatch;
  }

  get showInvalidEmailError(): boolean {
    const emailAddressControl = this.emailConfirmForm.get('emailAddress');
    const confirmEmailAddressControl = this.emailConfirmForm.get('confirmEmailAddress');
    const shouldShowError =
      (emailAddressControl?.dirty || emailAddressControl?.touched || false) &&
      (confirmEmailAddressControl?.dirty || confirmEmailAddressControl?.touched || false);
    const hasInvalidEmail = !!(emailAddressControl?.hasError('email') || confirmEmailAddressControl?.hasError('email'));
    return shouldShowError && this.emailsMatch && hasInvalidEmail;
  }

  openEmailConfirmDialog() {
    if (!this.bindForm.valid) {
      return;
    }

    this.emailConfirmForm.reset();
    this.showEmailConfirmDialog = true;
  }

  cancelEmailConfirmDialog() {
    this.showEmailConfirmDialog = false;
    this.emailConfirmForm.reset();
  }

  proceedToPaymentFromDialog() {
    if (!this.emailConfirmForm.valid || !this.emailsMatch) {
      this.emailConfirmForm.markAllAsTouched();
      return;
    }

    // Save email to the store
    this.policyStore.portalEmailId = this.emailConfirmForm.get('emailAddress')?.value;
    this.billingStoreService.setBillingStore(this.policyStore);

    this.showEmailConfirmDialog = false;
    this.goToPayment();
  }

  goToPayment() {
    if (this.testManualQuoteStatus && this.testManualQuoteStatus !== 'QTD') {
      this.policyStore.policyData.policy.quoteStatus = this.testManualQuoteStatus;
    }

    if (this.quotePreparationService.isQuoteInStatus(this.policyStore, 'QTD')) {
      this.router.navigate(['direct', 'payment']);
      return;
    }

    this.waitingForQtd = true;
    this.quotePreparationService.setCurrentQuoteStatus(this.quotePreparationService.getQuoteStatus(this.policyStore));
    this.quotePreparationService.setIPGPreparing('Preparing Quote for Payment', {
      pollForStatus: true,
      pollDelayMs: 5000,
      targetQuoteStatus: 'QTD',
      useLegacyRefreshWhenFlagOff: false,
      immediateCheckOnStart: true,
    });
  }

  cancelConfirm() {
    this.router.navigate(['direct', 'policy', this.policyStore.product, btoa(this.policyStore.id)]);
  }

  ngOnDestroy(): void {
    this.waitingForQtd = false;
    this.quotePreparationService.stopIPGPreparing();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
